import { useState } from 'react'
import { Link } from 'react-router-dom'
import { DeviceMobile, Prohibit, UserSwitch } from '@phosphor-icons/react'
import {
  Banner,
  Button,
  ChoiceGroup,
  DataTable,
  EmptyState,
  Field,
  Input,
  Modal,
  Select,
  type Column,
} from '@/ui'
import { errorMessage } from '../api/core'
import { useVillages, type UserOption } from '../api/reference'
import {
  useDevice,
  useDeviceTeam,
  useDevices,
  useRegisterDevice,
  useRevokeDevice,
  useUpdateDeviceOperator,
  type DeviceRow,
} from '../api/setup'
import { useSites } from '../api/store'
import { useDeviceSyncHealth } from '../api/sync'
import { ActionButton } from '../components/Can'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { DetailDrawer } from '../components/DetailDrawer'
import { FilterBar, SelectFilter } from '../components/FilterBar'
import { ConsolePage, QueryState } from '../components/Layout'
import { PersonPicker } from '../components/PersonPicker'
import { PhoneTeamSection } from '../components/PhoneTeam'
import { Code, Facts, Status, When } from '../components/bits'
import { humanize } from '../labels'
import { useUrlState } from '../lib/useUrlState'
import {
  deviceBody,
  emptyDeviceForm,
  normalizeDeviceCode,
  normalizePrefix,
  shortDeviceCode,
  validateDeviceForm,
  type DeviceForm,
  type DeviceTeamKind,
} from '../model/deviceForm'

const LIMIT = 25

/** Devices (F4.1-d): register phones to a village or a store site, see who uses them, revoke. */
export function DevicesPage() {
  const [f, setF, { reset, active }] = useUrlState({
    groupId: '',
    locationId: '',
    state: '',
    page: 1,
    open: '',
  })
  const [registering, setRegistering] = useState(false)
  const villages = useVillages()
  const sites = useSites()
  const q = useDevices({
    page: f.page,
    limit: LIMIT,
    groupId: f.groupId || undefined,
    locationId: f.locationId || undefined,
    revoked: f.state === 'REVOKED' ? true : f.state === 'ACTIVE' ? false : undefined,
  })
  const health = useDeviceSyncHealth()

  const columns: Column<DeviceRow>[] = [
    {
      id: 'prefix',
      header: 'Code prefix',
      cell: (r) => <span className="font-mono text-[15px] font-bold">{r.codePrefix}</span>,
      width: '110px',
    },
    { id: 'team', header: 'Team', cell: (r) => teamName(r) },
    {
      id: 'kind',
      header: 'Works for',
      cell: (r) => <span className="text-[13px] text-ink-soft">{teamKind(r)}</span>,
      hideBelow: 'sm',
    },
    {
      id: 'operator',
      header: 'Usual operator',
      cell: (r) => r.operatorName ?? <span className="text-muted">Anyone on the team</span>,
      hideBelow: 'sm',
    },
    {
      id: 'uid',
      header: 'Phone code',
      cell: (r) => <Code className="text-muted">{shortDeviceCode(r.deviceUid)}</Code>,
      hideBelow: 'md',
    },
    {
      id: 'version',
      header: 'App',
      cell: (r) => <Code>{r.appVersion ?? '—'}</Code>,
      hideBelow: 'lg',
    },
    ...(health.data
      ? [
          {
            id: 'sync',
            header: 'Last sync',
            cell: (r: DeviceRow) => {
              const h = health.data?.byDevice.get(r.id)
              return (
                <span className="flex flex-col items-start gap-0.5">
                  <When at={h?.lastCommandAt ?? null} relative />
                  {h && h.openProblems > 0 && (
                    <Link
                      to={`/c/sync-problems?deviceId=${r.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-[12.5px] font-medium text-danger hover:underline"
                    >
                      {h.openProblems} sync {h.openProblems === 1 ? 'problem' : 'problems'}
                    </Link>
                  )}
                </span>
              )
            },
          } satisfies Column<DeviceRow>,
        ]
      : []),
    {
      id: 'registered',
      header: 'Registered',
      cell: (r) => <When at={r.registeredAt} dateOnly />,
      hideBelow: 'md',
    },
    {
      id: 'status',
      header: 'Status',
      cell: (r) => (
        <span className="flex flex-col items-start gap-0.5">
          <Status status={r.status} />
          {r.revokeReason && (
            <span
              className="max-w-[16rem] truncate text-[12.5px] text-muted"
              title={r.revokeReason}
            >
              {r.revokeReason}
            </span>
          )}
        </span>
      ),
    },
  ]

  return (
    <ConsolePage
      eyebrow="Setup"
      title="Devices"
      description="Phones that work at the farm gate or in a store. Each phone belongs to one village or one site, and every code it makes starts with its code prefix."
      actions={
        <ActionButton
          perm="devices:create"
          leadingIcon={<DeviceMobile />}
          onClick={() => setRegistering(true)}
        >
          Register a phone
        </ActionButton>
      }
    >
      {(health.data?.openProblems ?? 0) > 0 && (
        <Banner
          tone="warn"
          title={`${health.data!.openProblems} phone ${health.data!.openProblems === 1 ? 'entry was' : 'entries were'} not saved`}
          action={
            <Link to="/c/sync-problems" className="font-medium underline">
              See sync problems
            </Link>
          }
        >
          The server did not save some entries the phones sent. Most clear once the other phone syncs;
          the rest need a look.
        </Banner>
      )}
      <HowTo />
      <DataTable<DeviceRow>
        caption="Phones"
        columns={columns}
        rows={q.data?.rows ?? []}
        getRowId={(r) => r.id}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        pageSize={LIMIT}
        server={{ page: f.page, total: q.data?.total ?? 0, onPageChange: (page) => setF({ page }) }}
        onRowClick={(r) => setF({ open: r.id, page: f.page })}
        filters={
          <FilterBar onClear={reset} active={active}>
            <SelectFilter
              label="Village"
              value={f.groupId}
              onChange={(groupId) => setF({ groupId, locationId: '' })}
              options={(villages.data ?? []).map((v) => ({ value: v.id, label: v.name }))}
            />
            <SelectFilter
              label="Store or site"
              value={f.locationId}
              onChange={(locationId) => setF({ locationId, groupId: '' })}
              options={(sites.data ?? []).map((s) => ({
                value: s.id,
                label: `${s.name} · ${s.code}`,
              }))}
            />
            <SelectFilter
              label="Status"
              value={f.state as '' | 'ACTIVE' | 'REVOKED'}
              onChange={(state) => setF({ state })}
              options={[
                { value: 'ACTIVE', label: 'Active' },
                { value: 'REVOKED', label: 'Revoked' },
              ]}
            />
          </FilterBar>
        }
        empty={
          <EmptyState
            icon={<DeviceMobile />}
            title={active ? 'No phones match' : 'No phones registered yet'}
            description={
              active
                ? 'Try clearing the filters.'
                : 'Register the first phone with the code it shows on its setup screen.'
            }
          />
        }
      />
      <RegisterDialog open={registering} onClose={() => setRegistering(false)} />
      <DeviceDrawer id={f.open || null} onClose={() => setF({ open: '', page: f.page })} />
    </ConsolePage>
  )
}

const teamName = (d: Pick<DeviceRow, 'groupName' | 'locationName'>) => d.groupName ?? d.locationName ?? '—'
const teamKind = (d: Pick<DeviceRow, 'groupId'>) => (d.groupId ? 'Village' : 'Store or site')

/** The three steps, always on the page: what the office does and what happens on the phone. */
function HowTo() {
  const steps = [
    {
      n: 1,
      title: 'Open the app on the phone',
      body: 'It opens on “Set up this phone” and shows a Phone code — a long code of letters and digits.',
    },
    {
      n: 2,
      title: 'Type that code here',
      body: 'Choose the village or the store the phone works for. Spaces and capital letters do not matter.',
    },
    {
      n: 3,
      title: 'Finish on the phone',
      body: 'Tap “Check registration”, then “Update farmer list” (on a store phone: “Update store list”). The team can now sign in with their PIN.',
    },
  ]
  return (
    <section
      aria-labelledby="register-howto"
      className="rounded-card border border-rule bg-surface px-4 py-4"
    >
      <h2 id="register-howto" className="text-[17px] leading-tight text-ink">
        How to register a phone
      </h2>
      <ol className="mt-3 grid gap-3 md:grid-cols-3">
        {steps.map((s) => (
          <li key={s.n} className="flex gap-3">
            <span
              aria-hidden="true"
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-wash font-mono text-[13px] font-bold text-amber-dark"
            >
              {s.n}
            </span>
            <span className="min-w-0">
              <span className="block text-[14px] font-medium text-ink">{s.title}</span>
              <span className="mt-0.5 block text-[13px] text-muted">{s.body}</span>
            </span>
          </li>
        ))}
      </ol>
    </section>
  )
}

function RegisterDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [form, setForm] = useState<DeviceForm>(emptyDeviceForm)
  const [operator, setOperator] = useState<UserOption | null>(null)
  const [touched, setTouched] = useState(false)
  const [done, setDone] = useState<DeviceRow | null>(null)
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setForm(emptyDeviceForm())
      setOperator(null)
      setTouched(false)
      setDone(null)
    }
  }
  const villages = useVillages()
  const sites = useSites()
  const register = useRegisterDevice()
  const errors = validateDeviceForm(form)
  const valid = Object.keys(errors).length === 0
  const set = (patch: Partial<DeviceForm>) => setForm((f) => ({ ...f, ...patch }))
  const uid = normalizeDeviceCode(form.deviceCode)

  const submit = async () => {
    setTouched(true)
    if (!valid) return
    try {
      setDone(await register.mutateAsync(deviceBody({ ...form, operatorId: operator?.id ?? null })))
    } catch {
      /* toasted with the server's reason */
    }
  }

  if (done) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        title="Phone registered"
        footer={
          <Button className="ml-auto" onClick={onClose}>
            Done
          </Button>
        }
      >
        <div className="flex flex-col items-center gap-2 py-2 text-center">
          <div className="font-mono text-[11px] font-bold uppercase tracking-label text-muted">
            Code prefix
          </div>
          <div className="rounded-card border border-amber/40 bg-amber-wash px-6 py-3 font-mono text-[44px] font-bold leading-none text-amber-dark">
            {done.codePrefix}
          </div>
          <p className="mt-2 text-[14px] text-ink">
            {done.groupName ?? done.locationName ?? 'The team'} · phone{' '}
            <Code>{shortDeviceCode(done.deviceUid)}</Code>
          </p>
          <p className="max-w-sm text-[13px] text-muted">
            Every session, lot and trip code this phone makes starts with {done.codePrefix}. Now, on
            the phone, tap “Check registration” and then “
            {done.groupId ? 'Update farmer list' : 'Update store list'}”.
          </p>
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Register a phone"
      description="Type the Phone code shown on the phone's setup screen."
      dismissible={!register.isPending}
      footer={
        <>
          <Button
            variant="ghost"
            className="ml-auto"
            onClick={onClose}
            disabled={register.isPending}
          >
            Cancel
          </Button>
          <Button loading={register.isPending} onClick={() => void submit()}>
            Register phone
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
      >
        <Field
          label="Phone code"
          required
          error={touched ? errors.deviceCode : null}
          hint={
            uid && uid !== form.deviceCode.trim()
              ? `Will be saved as ${uid}`
              : 'For example 0192f3c4-8a1b-7e2d-9c3f-4a5b6c7d8e9f'
          }
        >
          <Input
            value={form.deviceCode}
            onChange={(e) => set({ deviceCode: e.target.value })}
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            className="font-mono text-[15px]"
            data-autofocus
          />
        </Field>
        <Field label="Works for" required>
          <ChoiceGroup<DeviceTeamKind>
            value={form.teamKind}
            onChange={(teamKind) => set({ teamKind })}
            options={[
              { value: 'VILLAGE', label: 'A village' },
              { value: 'SITE', label: 'A store or site' },
            ]}
          />
        </Field>
        {form.teamKind === 'VILLAGE' ? (
          <Field label="Village" required error={touched ? errors.team : null}>
            <Select
              value={form.groupId}
              onChange={(groupId) => set({ groupId })}
              placeholder={villages.isLoading ? 'Loading…' : 'Choose the village'}
              options={(villages.data ?? []).map((v) => ({
                value: v.id,
                label: v.hint ? `${v.name} · ${v.hint}` : v.name,
              }))}
            />
          </Field>
        ) : (
          <Field label="Store or site" required error={touched ? errors.team : null}>
            <Select
              value={form.locationId}
              onChange={(locationId) => set({ locationId })}
              placeholder={sites.isLoading ? 'Loading…' : 'Choose the store or site'}
              options={(sites.data ?? [])
                .filter((s) => s.active)
                .map((s) => ({
                  value: s.id,
                  label: `${s.name} · ${s.code}${s.kind ? ` · ${humanize(s.kind)}` : ''}`,
                }))}
            />
          </Field>
        )}
        <Field
          label="Usual operator"
          hint="Optional. Anyone on the team can still sign in on the phone with their PIN."
        >
          <PersonPicker
            value={operator}
            onChange={setOperator}
            placeholder="Search by name"
            label="Usual operator"
          />
        </Field>
        <Field
          label="Code prefix"
          error={touched ? errors.codePrefix : null}
          hint="Optional. Leave empty and the next free prefix (A1, A2 …) is given. A prefix is never used twice."
        >
          <Input
            value={form.codePrefix}
            onChange={(e) => set({ codePrefix: normalizePrefix(e.target.value) })}
            maxLength={4}
            autoComplete="off"
            className="w-28 font-mono uppercase"
            placeholder="Auto"
          />
        </Field>
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </Modal>
  )
}

function DeviceDrawer({ id, onClose }: { id: string | null; onClose: () => void }) {
  const q = useDevice(id)
  const d = q.data
  const [dialog, setDialog] = useState<'revoke' | 'operator' | null>(null)
  const revoke = useRevokeDevice()
  return (
    <DetailDrawer
      open={Boolean(id)}
      onClose={onClose}
      eyebrow="Phone"
      title={d ? <span className="font-mono">{d.codePrefix}</span> : 'Phone'}
      meta={d && <Status status={d.status} />}
      actions={
        d &&
        d.status === 'ACTIVE' && (
          <>
            <ActionButton
              perm="devices:update"
              variant="secondary"
              leadingIcon={<UserSwitch />}
              onClick={() => setDialog('operator')}
            >
              Change operator
            </ActionButton>
            <ActionButton
              perm="devices:revoke"
              variant="danger"
              leadingIcon={<Prohibit />}
              onClick={() => setDialog('revoke')}
            >
              Revoke
            </ActionButton>
          </>
        )
      }
    >
      <QueryState query={q} label="the phone">
        {d && (
          <div className="flex flex-col gap-5">
            {d.status === 'REVOKED' && (
              <Banner tone="error" title="Revoked">
                {d.revokeReason && (
                  <span className="mb-1 block font-semibold">Reason: {d.revokeReason}</span>
                )}
                This phone was told to wipe its {d.groupId ? 'farmer list' : 'store list'} and PINs.
                Anything it still sends is kept but held for review. To use it again, reinstall the
                app and register the new code.
              </Banner>
            )}
            <Facts
              columns={2}
              items={[
                { label: 'Team', value: teamName(d) },
                { label: 'Works for', value: teamKind(d) },
                { label: 'Code prefix', value: d.codePrefix, mono: true },
                { label: 'Usual operator', value: d.operatorName ?? 'Anyone on the team' },
                { label: 'App version', value: d.appVersion ?? '—', mono: true },
                { label: 'Registered', value: <When at={d.registeredAt} /> },
                { label: 'Revoked', value: <When at={d.revokedAt} /> },
                ...(d.status === 'REVOKED'
                  ? [{ label: 'Reason for revoking', value: d.revokeReason ?? 'Not given' }]
                  : []),
              ]}
            />
            <Facts columns={2} items={[{ label: 'Phone code', value: d.deviceUid, mono: true }]} />
            {d.status === 'ACTIVE' && <PhoneTeamSection device={d} />}
          </div>
        )}
      </QueryState>
      {d && (
        <>
          <ConfirmDialog
            open={dialog === 'revoke'}
            onClose={() => setDialog(null)}
            title={`Revoke phone ${d.codePrefix}?`}
            description="Use this when a phone is lost, stolen or replaced."
            confirmLabel="Revoke phone"
            tone="danger"
            loading={revoke.isPending}
            reason={{
              label: 'Reason',
              placeholder: 'e.g. Phone lost on 18 Oct',
              minLength: 3,
              multiline: false,
            }}
            onConfirm={(reason) =>
              revoke
                .mutateAsync({ id: d.id, reason: reason.slice(0, 200) })
                .then(() => setDialog(null))
            }
          >
            <ul className="list-disc space-y-1 pl-5 text-[14px] text-ink">
              <li>
                The next time the phone connects it wipes its farmer list, PINs and saved site data.
              </li>
              <li>Anything it sends after this is kept but held for a supervisor to check.</li>
              <li>
                This cannot be undone. To use the same phone again, reinstall the app and register
                its new code.
              </li>
            </ul>
          </ConfirmDialog>
          <OperatorDialog open={dialog === 'operator'} onClose={() => setDialog(null)} device={d} />
        </>
      )}
    </DetailDrawer>
  )
}

function OperatorDialog({
  open,
  onClose,
  device,
}: {
  open: boolean
  onClose: () => void
  device: DeviceRow
}) {
  const [personId, setPersonId] = useState('')
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) setPersonId('')
  }
  const update = useUpdateDeviceOperator()
  const team = useDeviceTeam(device.id, open)
  const save = (operatorId: string | null) =>
    update
      .mutateAsync({ id: device.id, operatorId })
      .then(onClose)
      .catch(() => undefined)
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Usual operator of ${device.codePrefix}`}
      description={`Now: ${device.operatorName ?? 'no one in particular'}. Anyone on the team can still sign in with their PIN.`}
      dismissible={!update.isPending}
      footer={
        <>
          {device.operatorId && (
            <Button variant="ghost" onClick={() => void save(null)} disabled={update.isPending}>
              Remove operator
            </Button>
          )}
          <Button variant="ghost" className="ml-auto" onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
          <Button
            disabled={!personId}
            loading={update.isPending}
            onClick={() => personId && void save(personId)}
          >
            Save
          </Button>
        </>
      }
    >
      <Field
        label="Operator"
        required
        hint="Only people on this phone's team. Add someone under Team on this phone first."
      >
        <Select
          value={personId}
          onChange={setPersonId}
          placeholder={team.isLoading ? 'Loading…' : 'Choose a person on the team'}
          options={(team.data ?? []).map((m) => ({ value: m.userId, label: m.name }))}
        />
      </Field>
    </Modal>
  )
}
